import { useLocalization } from "@fluent/react";
import { animated } from "@react-spring/web";
import { onboarding } from "byzantine";

import { ThemedSvg } from "../../../components/ThemedSvg";

import { useExistingUserSidebarGuidesContainer } from "./containers";
import { CAUGHT_UP_SENTINEL } from "./hooks/useCardStack";
import styles from "./ExistingUserSidebarGuides.module.scss";

import type { OnboardingPrompt } from "byzantine/src/dbbl/businessLogic/modules/onboarding";

const { PROMPT_SVG_DATA, CAUGHT_UP_SENTINEL_UUID } = onboarding;

const CaughtUpCard = ({ label }: { label: string }) => (
  <div className={styles.caughtUpCard}>
    <ThemedSvg
      iconSvg={PROMPT_SVG_DATA[CAUGHT_UP_SENTINEL_UUID] || ""}
      size={56}
    />
    <p className={styles.caughtUpText}>{label}</p>
  </div>
);

const ExistingUserSidebarGuides = ({
  isCollapsed,
}: {
  isCollapsed: boolean;
}) => {
  const { l10n } = useLocalization();

  const {
    isExistingGuide,
    rawPrompts,
    stackedPrompts,
    dismissingUuid,
    dismissSpring,
    dismissPrompt,
    caughtUpSpring,
    newEntryUuids,
    getCardStyle,
    handlePromptClick,
    renderDialogs,
  } = useExistingUserSidebarGuidesContainer();

  if (!isExistingGuide || !rawPrompts.length) {
    return null;
  }

  const containerClasses = `${styles.guideContainer} ${
    isCollapsed ? styles["guideContainer--hidden"] : ""
  }`;

  return (
    <div className={containerClasses}>
      <div className={styles.cardStack}>
        {stackedPrompts.map((entry, index) => {
          const isCaughtUp = entry === CAUGHT_UP_SENTINEL;
          const isTop = index === 0;
          const isBeingDismissed =
            !isCaughtUp && (entry as OnboardingPrompt).uuid === dismissingUuid;
          const cardStyle = getCardStyle(index, isBeingDismissed);

          if (isCaughtUp) {
            return (
              <animated.div
                key={CAUGHT_UP_SENTINEL}
                className={!isTop ? styles.cardEnter : undefined}
                style={{
                  ...cardStyle,
                  opacity: caughtUpSpring.opacity,
                }}
              >
                <CaughtUpCard
                  label={l10n.getString("sidebar-guide-all-caught-up")}
                />
              </animated.div>
            );
          }

          const prompt = entry as OnboardingPrompt;
          const isNewEntry = newEntryUuids.has(prompt.uuid) && !isTop;

          return (
            <animated.div
              key={prompt.uuid}
              className={isNewEntry ? styles.cardEnter : undefined}
              style={{
                ...cardStyle,
                opacity: isBeingDismissed ? dismissSpring.opacity : 1,
                transform: isBeingDismissed ? dismissSpring.transform : "none",
              }}
            >
              <div className={styles.promptCard}>
                <div>
                  <div className={styles.promptTitle}>{prompt.title}</div>
                  <div className={styles.promptDescription}>
                    {prompt.description}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.promptCta}
                  onClick={() => handlePromptClick(prompt)}
                  aria-label={prompt.link_text}
                >
                  {prompt.link_text || ""}
                </button>
                <div className={styles.illustrationWrapper}>
                  <div className={styles.illustrationInner}>
                    <ThemedSvg
                      iconSvg={PROMPT_SVG_DATA[prompt.uuid] ?? ""}
                      size={64}
                    />
                  </div>
                </div>
                {!dismissingUuid && (
                  <button
                    type="button"
                    className={styles.dismissButton}
                    onClick={() => dismissPrompt(prompt.uuid)}
                    aria-label={l10n.getString("sidebar-guide-dismiss")}
                  >
                    <span className="narmi-icon-x" />
                  </button>
                )}
              </div>
            </animated.div>
          );
        })}
      </div>
      {renderDialogs}
    </div>
  );
};

export default ExistingUserSidebarGuides;
